import React, { useEffect, useRef } from 'react';

/* Pure CSS 3D animated logistics globe - no WebGL dependencies */
export const LogisticsGlobe3D = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let time = 0;

    const resize = () => {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * (window.devicePixelRatio > 1 ? 1.5 : 1);
      canvas.height = rect.height * (window.devicePixelRatio > 1 ? 1.5 : 1);
      canvas.style.width = rect.width + 'px';
      canvas.style.height = rect.height + 'px';
    };
    resize();
    window.addEventListener('resize', resize);

    const cx = () => canvas.width / 2;
    const cy = () => canvas.height / 2;
    const R = () => Math.min(canvas.width, canvas.height) * 0.28;

    /* Project 3D point to 2D */
    const project = (lon, lat, r) => {
      const x = r * Math.cos(lat) * Math.sin(lon);
      const y = r * Math.sin(lat);
      const z = r * Math.cos(lat) * Math.cos(lon);
      const scale = 1 / (1 + z / (r * 4));
      return { x: cx() + x * scale, y: cy() - y * scale, z, scale };
    };

    /* Draw wireframe globe */
    const drawGlobe = (t) => {
      const r = R();
      ctx.strokeStyle = 'rgba(245, 158, 11, 0.12)';
      ctx.lineWidth = 0.8;

      // Meridians
      for (let i = 0; i < 12; i++) {
        ctx.beginPath();
        const lonOff = t * 0.3 + (i * Math.PI) / 6;
        for (let j = -90; j <= 90; j += 3) {
          const p = project(lonOff, (j * Math.PI) / 180, r);
          j === -90 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }

      // Parallels
      for (let i = -3; i <= 3; i++) {
        ctx.beginPath();
        const lat = (i * 25 * Math.PI) / 180;
        for (let j = 0; j <= 360; j += 3) {
          const p = project((j * Math.PI) / 180 + t * 0.3, lat, r);
          j === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }

      // Globe glow
      const grd = ctx.createRadialGradient(cx(), cy(), r * 0.2, cx(), cy(), r * 1.3);
      grd.addColorStop(0, 'rgba(245, 158, 11, 0.06)');
      grd.addColorStop(1, 'rgba(245, 158, 11, 0)');
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(cx(), cy(), r * 1.3, 0, Math.PI * 2);
      ctx.fill();
    };

    /* Draw orbit rings */
    const drawOrbitRing = (t, radius, speed, tilt, color) => {
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = 0; i <= 360; i += 2) {
        const angle = (i * Math.PI) / 180 + t * speed;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius * Math.sin(tilt);
        const z = Math.sin(angle) * radius * Math.cos(tilt);
        const scale = 1 / (1 + z / (radius * 3));
        const px = cx() + x * scale;
        const py = cy() - y * scale;
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    };

    /* Draw orbiting package (cube look) */
    const drawPackage = (t, radius, speed, offset, color, tilt) => {
      const angle = t * speed + offset;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius * Math.sin(tilt) + Math.sin(angle * 2) * 8;
      const z = Math.sin(angle) * radius * Math.cos(tilt);
      const scale = 1 / (1 + z / (radius * 3));
      const px = cx() + x * scale;
      const py = cy() - y * scale;
      const sz = 8 * scale;

      // Package glow
      const glow = ctx.createRadialGradient(px, py, 0, px, py, sz * 3);
      glow.addColorStop(0, color.replace('1)', '0.3)'));
      glow.addColorStop(1, 'transparent');
      ctx.fillStyle = glow;
      ctx.fillRect(px - sz * 3, py - sz * 3, sz * 6, sz * 6);

      // 3D box faces
      const rot = t * 3;
      ctx.save();
      ctx.translate(px, py);
      ctx.rotate(rot);
      
      // Front face
      ctx.fillStyle = color;
      ctx.fillRect(-sz, -sz, sz * 2, sz * 2);
      // Top face (lighter)
      ctx.fillStyle = color.replace('1)', '0.6)');
      ctx.beginPath();
      ctx.moveTo(-sz, -sz);
      ctx.lineTo(-sz + sz * 0.5, -sz - sz * 0.5);
      ctx.lineTo(sz + sz * 0.5, -sz - sz * 0.5);
      ctx.lineTo(sz, -sz);
      ctx.fill();
      // Side face (darker)
      ctx.fillStyle = color.replace('1)', '0.4)');
      ctx.beginPath();
      ctx.moveTo(sz, -sz);
      ctx.lineTo(sz + sz * 0.5, -sz - sz * 0.5);
      ctx.lineTo(sz + sz * 0.5, sz - sz * 0.5);
      ctx.lineTo(sz, sz);
      ctx.fill();
      ctx.restore();
    };

    /* Draw location pin */
    const drawPin = (t, lon, lat) => {
      const r = R();
      const p = project(lon + t * 0.3, lat, r);
      if (p.z < 0) return; // behind globe

      const pulse = 1 + Math.sin(t * 4 + lon) * 0.3;

      // Pulse ring
      ctx.strokeStyle = `rgba(239, 68, 68, ${0.3 / pulse})`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 12 * pulse * p.scale, 0, Math.PI * 2);
      ctx.stroke();

      // Pin dot
      ctx.fillStyle = '#ef4444';
      ctx.shadowColor = '#ef4444';
      ctx.shadowBlur = 10;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4 * p.scale, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;
    };

    /* Draw delivery arc */
    const drawArc = (t, from, to, color) => {
      const r = R();
      const steps = 40;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.lineDashOffset = -t * 30;
      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const frac = i / steps;
        const lon = from[0] + (to[0] - from[0]) * frac + t * 0.3;
        const lat = from[1] + (to[1] - from[1]) * frac;
        const lift = Math.sin(frac * Math.PI) * 0.5;
        const p = project(lon, lat + lift, r);
        i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y);
      }
      ctx.stroke();
      ctx.setLineDash([]);
    };

    /* Floating particles */
    const particles = Array.from({ length: 50 }, () => ({
      x: Math.random() * 2 - 1,
      y: Math.random() * 2 - 1,
      speed: 0.2 + Math.random() * 0.8,
      size: 0.5 + Math.random() * 1.5,
      opacity: 0.1 + Math.random() * 0.4
    }));

    const drawParticles = (t) => {
      particles.forEach(p => {
        const px = cx() + p.x * canvas.width * 0.45 + Math.sin(t * p.speed) * 20;
        const py = cy() + p.y * canvas.height * 0.45 + Math.cos(t * p.speed * 1.3) * 15;
        ctx.fillStyle = `rgba(245, 158, 11, ${p.opacity})`;
        ctx.beginPath();
        ctx.arc(px, py, p.size, 0, Math.PI * 2);
        ctx.fill();
      });
    };

    /* City pin locations (lon, lat in radians) */
    const cities = [
      [1.2, 0.3], [-0.8, 0.5], [0.4, -0.4], [-0.3, 0.8],
      [1.8, -0.2], [-1.5, 0.1]
    ];

    /* Delivery routes between cities */
    const routes = [
      { from: [1.2, 0.3], to: [-0.8, 0.5], color: 'rgba(245, 158, 11, 0.5)' },
      { from: [-0.8, 0.5], to: [0.4, -0.4], color: 'rgba(59, 130, 246, 0.5)' },
      { from: [0.4, -0.4], to: [-0.3, 0.8], color: 'rgba(34, 197, 94, 0.5)' },
    ];

    const animate = () => {
      time += 0.008;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      drawParticles(time);
      drawGlobe(time);

      // Orbit rings
      const rr = R();
      drawOrbitRing(time, rr * 1.45, 0.3, 0.5, 'rgba(245, 158, 11, 0.15)');
      drawOrbitRing(time, rr * 1.7, -0.2, -0.35, 'rgba(59, 130, 246, 0.12)');
      drawOrbitRing(time, rr * 1.95, 0.15, 0.75, 'rgba(168, 85, 247, 0.1)');

      // Delivery arcs
      routes.forEach(route => drawArc(time, route.from, route.to, route.color));

      // Location pins
      cities.forEach(c => drawPin(time, c[0], c[1]));

      // Orbiting packages
      drawPackage(time, rr * 1.45, 0.7, 0, 'rgba(245, 158, 11, 1)', 0.5);
      drawPackage(time, rr * 1.7, -0.5, 2, 'rgba(59, 130, 246, 1)', -0.35);
      drawPackage(time, rr * 1.95, 0.35, 4, 'rgba(34, 197, 94, 1)', 0.75);

      animId = requestAnimationFrame(animate);
    };

    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section className="relative py-20 bg-[#050505] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(245,158,11,0.06)_0%,_transparent_70%)]" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="space-y-6 z-10">
            <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-500 rounded-full text-sm font-bold tracking-wider">GLOBAL NETWORK</span>
            <h2 className="font-outfit text-4xl lg:text-5xl font-black text-white leading-tight">
              Connecting Every
              <span className="block bg-gradient-to-r from-amber-400 via-orange-400 to-red-400 bg-clip-text text-transparent">Corner of India</span>
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed max-w-lg">
              Our logistics network spans the entire nation. Real-time tracking, optimized routes, and dedicated fleet management — all powered by cutting-edge technology.
            </p>
            <div className="flex gap-8 pt-4">
              <div><p className="text-3xl font-black text-amber-500">500+</p><p className="text-sm text-slate-500 font-medium">Routes</p></div>
              <div><p className="text-3xl font-black text-blue-400">100+</p><p className="text-sm text-slate-500 font-medium">Vehicles</p></div>
              <div><p className="text-3xl font-black text-green-400">24/7</p><p className="text-sm text-slate-500 font-medium">Tracking</p></div>
            </div>
          </div>

          <div className="relative h-[420px] lg:h-[480px] w-full" data-testid="3d-globe">
            <canvas ref={canvasRef} className="w-full h-full" />
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-[#0B0F19] to-transparent" />
    </section>
  );
};
